import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLES, esAdmin, nombreRol } from "@/lib/roles";
import { codigoValido, necesitaSegundoFactor } from "@/lib/segundoFactor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";
const fecha = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Users() {
  const { user, replaceToken } = useAuth();
  // Usuarios y roles son del administrador; el operador solo cambia su contraseña («Mi cuenta» en el panel).
  const admin = esAdmin(user);
  const [users, setUsers] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "admin" });
  const [pwd, setPwd] = useState({ current: "", new: "", repeat: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setUsers((await api.get("/admin/users")).data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  // Si la instalación exige el segundo factor y aún no lo tiene, el resto le daría 403: solo ve cómo activarlo.
  const bloqueado = necesitaSegundoFactor(user);
  useEffect(() => { if (admin && !bloqueado) load(); }, [admin, bloqueado, load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", invite);
      toast.success(`Invitación enviada a ${invite.email}`);
      setInvite({ email: "", name: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const cambiarRol = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`${u.email}: ${nombreRol(role)}`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Quitar el acceso de ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const quitarSegundoFactor = async (u) => {
    if (!window.confirm(`¿Quitar el segundo factor de ${u.email}? Se cerrarán sus sesiones y podrá volver a activarlo.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/totp/reset`);
      toast.success(`Segundo factor de ${u.email} quitado`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new !== pwd.repeat) return toast.error("Las dos contraseñas nuevas no coinciden");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current: pwd.current, new: pwd.new });
      replaceToken(data.token);
      setPwd({ current: "", new: "", repeat: "" });
      toast.success("Contraseña cambiada; las demás sesiones se han cerrado");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-3xl space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">{admin ? "Usuarios" : "Mi cuenta"}</h1>
        <SegundoFactor admin={admin} />
        {admin && !bloqueado && (
          <section className="bg-white border border-[#DCD8D0] rounded-xl overflow-hidden" data-testid="users-list">
            <ul className="divide-y divide-[#DCD8D0]">
              {users.map((u) => (
                <li key={u.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`user-row-${u.email}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A1A1A] truncate">{u.name} · {u.email}</div>
                    <div className="text-xs text-[#8A8A8A]">
                      {u.pending ? "Invitación pendiente" : `Último acceso: ${fecha(u.last_login_at)}`}
                      {u.totp_enabled && " · Segundo factor activo"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {u.id !== user?.id ? (
                      <select value={u.role} onChange={(e) => cambiarRol(u, e.target.value)} aria-label={`Rol de ${u.email}`}
                              className="h-8 rounded-md border border-[#DCD8D0] bg-white px-2 text-xs" data-testid={`user-role-${u.email}`}>
                        {ROLES.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
                      </select>
                    ) : <span className="text-xs text-[#8A8A8A]">{nombreRol(u.role)}</span>}
                    {u.id !== user?.id && u.totp_enabled && (
                      <Button variant="outline" size="sm" onClick={() => quitarSegundoFactor(u)} data-testid={`user-2fa-reset-${u.email}`}>
                        Quitar segundo factor
                      </Button>
                    )}
                    {u.id !== user?.id && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} aria-label="Quitar acceso" data-testid={`user-delete-${u.email}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        {admin && !bloqueado && (
          <form onSubmit={sendInvite} className="bg-white border border-[#DCD8D0] rounded-xl p-6 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end" data-testid="users-invite">
            <div><Label htmlFor="inv-name">Nombre</Label><Input id="inv-name" required className={inputCls} value={invite.name} onChange={(e) => setInvite((d) => ({ ...d, name: e.target.value }))} data-testid="inv-name" /></div>
            <div><Label htmlFor="inv-email">Correo</Label><Input id="inv-email" type="email" required className={inputCls} value={invite.email} onChange={(e) => setInvite((d) => ({ ...d, email: e.target.value }))} data-testid="inv-email" /></div>
            <div>
              <Label htmlFor="inv-role">Rol</Label>
              <select id="inv-role" value={invite.role} onChange={(e) => setInvite((d) => ({ ...d, role: e.target.value }))}
                      className="mt-1 h-10 rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm" data-testid="inv-role">
                {ROLES.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
              </select>
            </div>
            <Button type="submit" disabled={busy} className="bg-marca hover:bg-marca-hover text-white" data-testid="inv-send">Invitar</Button>
            <p className="sm:col-span-4 text-xs text-[#8A8A8A]">
              Le llegará un correo para elegir su contraseña (el enlace vale 7 días). El operador lleva las reservas, las
              entregas y las devoluciones, y liquida la devolución, lo que devuelve o libera la fianza; los ajustes, los
              usuarios, las finanzas, las exportaciones, las devoluciones de cobros y anular o borrar reservas son del
              administrador.
            </p>
          </form>
        )}
        {!bloqueado && <form onSubmit={changePassword} className="bg-white border border-[#DCD8D0] rounded-xl p-6 grid sm:grid-cols-3 gap-3 items-end" data-testid="users-password">
          <h2 className="sm:col-span-3 font-display text-lg font-semibold">Mi contraseña</h2>
          <div><Label htmlFor="pw-cur">Actual</Label><Input id="pw-cur" type="password" required autoComplete="current-password" className={inputCls} value={pwd.current} onChange={(e) => setPwd((d) => ({ ...d, current: e.target.value }))} /></div>
          <div><Label htmlFor="pw-new">Nueva (10 caracteres o más)</Label><Input id="pw-new" type="password" required minLength={10} autoComplete="new-password" className={inputCls} value={pwd.new} onChange={(e) => setPwd((d) => ({ ...d, new: e.target.value }))} /></div>
          <div><Label htmlFor="pw-rep">Repite la nueva</Label><Input id="pw-rep" type="password" required minLength={10} autoComplete="new-password" className={inputCls} value={pwd.repeat} onChange={(e) => setPwd((d) => ({ ...d, repeat: e.target.value }))} /></div>
          <Button type="submit" disabled={busy} variant="outline" className="sm:col-span-3 w-fit">Cambiar contraseña</Button>
        </form>}
      </main>
    </div>
  );
}

// Segundo factor de quien está en el panel (backend/totp.py): activarlo con una app de autenticación (QR o clave),
// ver una sola vez los códigos de recuperación y desactivarlo con la contraseña y un código de la app o de
// recuperación (cambio de móvil; si es obligatorio, después solo se puede activar otra vez). Al administrador,
// además, el interruptor que lo hace obligatorio para todos.
function SegundoFactor({ admin }) {
  const { user, refresh, logout } = useAuth();
  // En la demostración no se puede activar ni exigir (backend: totp_available false): no se ofrece.
  const disponible = user?.totp_available !== false;
  const [alta, setAlta] = useState(null); // {secret, uri, qr} mientras se activa; nunca se guarda en el navegador
  const [codigo, setCodigo] = useState("");
  const [codigos, setCodigos] = useState(null); // códigos de recuperación: se enseñan una sola vez
  const [baja, setBaja] = useState({ password: "", code: "" });
  const [busy, setBusy] = useState(false);

  const empezar = async () => {
    setBusy(true);
    try {
      setAlta((await api.post("/auth/totp/setup")).data);
      setCodigo("");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const activar = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(codigo.replace(/\s/g, ""))) {
      toast.error("Escribe los 6 números que enseña la app");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/enable", { code: codigo.replace(/\s/g, "") });
      setAlta(null);
      setCodigo("");
      setCodigos(data.recovery_codes);
      toast.success("Segundo factor activado");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const desactivar = async (e) => {
    e.preventDefault();
    if (!codigoValido(baja.code)) {
      toast.error("Escribe los 6 números de la app o un código de recuperación (XXXX-XXXX)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/totp/disable", { password: baja.password, code: baja.code.replace(/\s/g, "") });
      setBaja({ password: "", code: "" });
      toast.success("Segundo factor desactivado");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-4" data-testid="users-2fa">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2">
        <ShieldCheck className="h-5 w-5" /> Segundo factor
      </h2>
      {necesitaSegundoFactor(user) && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="users-2fa-required-notice">
          <span>Esta instalación exige el segundo factor: actívalo para seguir usando el panel.</span>
          <Button variant="outline" size="sm" onClick={logout}>Salir</Button>
        </div>
      )}
      {codigos ? (
        <div className="space-y-3">
          <p className="text-sm text-[#1A1A1A]">
            Códigos de recuperación. Guárdalos: cada uno vale una vez y no se volverán a enseñar. Sirven para entrar si
            pierdes el móvil.
          </p>
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm" data-testid="users-2fa-codes">
            {codigos.map((c) => <li key={c} className="rounded border border-[#DCD8D0] bg-[#F7F5F0] px-3 py-1">{c}</li>)}
          </ul>
          <Button variant="outline" onClick={() => setCodigos(null)} data-testid="users-2fa-codes-done">Ya los he guardado</Button>
        </div>
      ) : user?.totp_enabled ? (
        <form onSubmit={desactivar} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="users-2fa-disable">
          <p className="sm:col-span-3 text-sm text-[#5C5C5C]">
            Activado: al entrar se pide, además de la contraseña, el código de tu app de autenticación. Para cambiar de
            móvil (o si lo has perdido), desactívalo con tu contraseña y un código de la app o un código de recuperación,
            y vuelve a activarlo.
          </p>
          <div><Label htmlFor="tf-off-pw">Contraseña</Label><Input id="tf-off-pw" type="password" required autoComplete="current-password" className={inputCls} value={baja.password} onChange={(e) => setBaja((d) => ({ ...d, password: e.target.value }))} data-testid="users-2fa-off-password" /></div>
          <div><Label htmlFor="tf-off-code">Código de la app o de recuperación</Label><Input id="tf-off-code" required autoComplete="one-time-code" maxLength={20} className={inputCls} value={baja.code} onChange={(e) => setBaja((d) => ({ ...d, code: e.target.value }))} data-testid="users-2fa-off-code" /></div>
          <Button type="submit" disabled={busy} variant="outline">Desactivar</Button>
        </form>
      ) : alta ? (
        <form onSubmit={activar} className="space-y-3" data-testid="users-2fa-enable">
          <p className="text-sm text-[#5C5C5C]">
            Lee este código con tu app de autenticación (Google Authenticator, Microsoft Authenticator, Aegis…) y escribe
            los 6 números que te enseña.
          </p>
          <img src={alta.qr} alt="Código QR para la app de autenticación" className="h-48 w-48 border border-[#DCD8D0] rounded" />
          <p className="text-xs text-[#5C5C5C]">
            Si no puedes leerlo, escribe esta clave en la app: <code className="font-mono break-all text-[#1A1A1A]" data-testid="users-2fa-secret">{alta.secret}</code>
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label htmlFor="tf-code">Código de la app</Label><Input id="tf-code" required inputMode="numeric" autoComplete="one-time-code" maxLength={7} className={inputCls} value={codigo} onChange={(e) => setCodigo(e.target.value)} data-testid="users-2fa-code" /></div>
            <Button type="submit" disabled={busy} className="bg-marca hover:bg-marca-hover text-white">Activar</Button>
            <Button type="button" variant="ghost" onClick={() => setAlta(null)}>Cancelar</Button>
          </div>
        </form>
      ) : !disponible ? (
        <p className="text-sm text-[#5C5C5C]">En la demostración no se usa el segundo factor.</p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#5C5C5C]">
            Además de la contraseña, al entrar se pedirá un código de una app de autenticación del móvil.
          </p>
          <Button onClick={empezar} disabled={busy} className="bg-marca hover:bg-marca-hover text-white" data-testid="users-2fa-start">Activar</Button>
        </div>
      )}
      {admin && disponible && !necesitaSegundoFactor(user) && <ObligatorioParaTodos activado={Boolean(user?.totp_enabled)} />}
    </section>
  );
}

// Solo para el administrador (a un operador, GET /admin/security le da 403: ni se pide).
function ObligatorioParaTodos({ activado }) {
  const [obligatorio, setObligatorio] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get("/admin/security")
      .then(({ data }) => vivo && setObligatorio(Boolean(data.totp_required)))
      .catch(() => vivo && setObligatorio(null));
    return () => { vivo = false; };
  }, [activado]);
  if (obligatorio === null) return null;

  const cambiar = async (valor) => {
    try {
      const { data } = await api.put("/admin/security", { totp_required: valor });
      setObligatorio(Boolean(data.totp_required));
      toast.success(valor ? "Ahora todos los usuarios necesitan el segundo factor" : "El segundo factor vuelve a ser opcional");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border-t border-[#DCD8D0] pt-4">
      <div>
        <Label htmlFor="tf-required">Obligatorio para todos</Label>
        <p className="text-xs text-[#8A8A8A]">Quien no lo tenga solo podrá activarlo al entrar. Antes, activa el tuyo.</p>
      </div>
      <Switch id="tf-required" checked={obligatorio} onCheckedChange={cambiar} data-testid="users-2fa-required" />
    </div>
  );
}
